import { useMemo } from 'react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/react';

// Types
import type { GetOrdersUPP_orders_OrdersListSuccess_ordersList } from 'gql/typeMigrations';

// Constants
import { PRODUCT_LOOKUP } from 'constants/consults';

// Components
import { Dropdown } from 'components/dropdown';

// Helpers
import { useGetOrders } from 'features/consults/components/participant-form/hooks';
import { getFormattedDate } from 'helpers/formatters';

export function formatDate(date?: string | null, defaultText = 'N/A'): string {
  if (date && !Number.isNaN(new Date(date).valueOf())) {
    const dt = DateTime.fromFormat(date, 'yyyy-MM-dd', {
      zone: 'America/Los_Angeles',
    });

    return dt.toLocaleString({
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  return defaultText;
}

export function formatFullName(
  firstName?: string | null,
  lastName?: string | null,
) {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }

  return firstName || lastName || undefined;
}

export function formatSex(sex?: string | null) {
  if (sex === 'F') {
    return 'Female';
  }

  if (sex === 'M') {
    return 'Male';
  }

  return sex || undefined;
}

type MyOrderTopic = NonNullable<ComponentProps<typeof Dropdown>['items']>[0] & {
  detail?: GetOrdersUPP_orders_OrdersListSuccess_ordersList;
};

export function useMyOrderDropdownOptions({ disablePending = false }) {
  const { data: myOrders } = useGetOrders();
  const { t } = useTranslation();

  const myOrderTopics: MyOrderTopic[] = useMemo(() => {
    if (
      myOrders?.orders?.__typename === 'OrdersListSuccess' &&
      myOrders.orders.ordersList
    ) {
      return myOrders.orders.ordersList
        .filter((order) => order.productId && order.productId in PRODUCT_LOOKUP)
        .map((order) => {
          let label = '';
          let value = '';

          if (order.productId && order.productId in PRODUCT_LOOKUP) {
            const productId = order.productId as keyof typeof PRODUCT_LOOKUP;

            label = PRODUCT_LOOKUP[productId].label;
            value = PRODUCT_LOOKUP[productId].value;
          } else if (order.productId) {
            Sentry.captureException(
              `Unsupported consults product: ${order.productId}`,
            );
          }

          return {
            key: order.externalId || undefined,
            label: label || '',
            description: order.resultsReleasedToPatient
              ? t('consultsUPP.contactInfo.topicOptionDescription', {
                  name: order.patientFirstName,
                  // If release date is undefined, falls back to empty string
                  date: getFormattedDate(
                    order.resultsReleasedToPatient,
                    'MM/dd/yyyy',
                  ),
                })
              : t('consultsUPP.contactInfo.topicOptionDescriptionNotReported', {
                  name: order.patientFirstName,
                }),
            value: value || '',
            disabled: disablePending && !order.resultsReleasedToPatient,
            detail: order,
          };
        })
        .sort((a, b) => {
          // Prefer sort by release date, otherwise sort by create date
          if (
            a.detail.resultsReleasedToPatient &&
            b.detail.resultsReleasedToPatient
          ) {
            return (
              new Date(b.detail.resultsReleasedToPatient).getTime() -
              new Date(a.detail.resultsReleasedToPatient).getTime()
            );
          }

          return (
            new Date(b.detail.createDate).getTime() -
            new Date(a.detail.createDate).getTime()
          );
        });
    }

    return [];
  }, [myOrders, t, disablePending]);

  return myOrderTopics;
}

export function isNumberConditions(value: string) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore-next-line
  // eslint-disable-next-line eqeqeq
  return parseInt(value, 10) == value;
}
